import { logger } from 'services';
/**
 * Defensive guard against React commit-phase crashes caused by DOM mutated
 * OUT-OF-BAND — anything that reparents/wraps nodes React owns: browser
 * auto-translation (Edge/Chrome wrap text nodes in `<font>`), browser
 * extensions, or any other script React doesn't know about. React's commit
 * phase later calls `parent.removeChild(textNode)` /
 * `parent.insertBefore(node, refNode)` on a node that is no longer a direct
 * child of `parent`, so the DOM throws `NotFoundError: ...not a child of this
 * node`, the ErrorBoundary re-renders, and the throw can repeat.
 *
 * Scope: this guard addresses the organic crash tail (~2/day fleet-wide:
 * translate/extension users, typically on login-type pages). The 2026-07-07..10
 * crash-loop storm on /plan routes was a separate issue — a Module Federation
 * dual-react-dom race from the rspack cutover, fixed by share-scope determinism
 * (`shareStrategy: 'loaded-first'` + `resolveShare` hook, PR #18708) and
 * detected by `utils/mf-singleton-telemetry`.
 *
 * `Node.prototype` is a single global shared by the host and every MF remote
 * in the same window, so patching it once here — before `createRoot` in
 * `src/index.tsx` — covers all React copies on the page.
 *
 * The patch makes the two methods no-op (return the node unchanged) when the
 * target is not actually a child of `this`, instead of throwing. This is the
 * canonical workaround authored by Dan Abramov (React core) in
 * facebook/react#11538 and shipped by many production React apps: the page
 * keeps working and React self-heals its fiber tree on the next full render.
 * It changes behaviour ONLY in the mismatched-parent case (which today already
 * throws), so well-behaved code — ours and third-party libraries — is a pure
 * pass-through and unaffected. Each suppression logs one throttled
 * `logger.warn` per page load ("dom-reconciliation-guard: suppressed ...") so
 * the signal is transformed (crash → warn), not lost — watch that warn series
 * in Datadog as the guard's residual detector.
 */ const GUARD_FLAG = '__rlDomTranslationGuardInstalled';
let hasWarned = false;
/**
 * Report the first suppression per page load only. Reporting every call would
 * re-create the exact per-render event storm this guard exists to stop, and
 * `logger.warn` forwards to Datadog. One warn keeps the signal without the
 * noise.
 */ const warnOnce = (method)=>{
    if (hasWarned) {
        return;
    }
    hasWarned = true;
    logger.warn(`dom-reconciliation-guard: suppressed a ${method} NotFoundError — likely browser auto-translation mutating React-owned DOM`, {
        method
    });
};
export const installDomReconciliationGuard = ()=>{
    if (typeof Node !== 'function' || !Node.prototype) {
        return;
    }
    const proto = Node.prototype;
    // Idempotent — never double-wrap (HMR, repeated imports, test re-runs).
    if (proto[GUARD_FLAG]) {
        return;
    }
    proto[GUARD_FLAG] = true;
    const originalRemoveChild = Node.prototype.removeChild;
    Node.prototype.removeChild = function guardedRemoveChild(child) {
        // Canonical gaearon patch (facebook/react#11538): when the node is not
        // actually a child of `this` (a translator reparented it), do NOT throw —
        // and do NOT move it either. Returning the node unchanged is the minimal,
        // battle-tested behaviour; React self-heals its fiber tree on the next
        // full render.
        if (child.parentNode !== this) {
            warnOnce('removeChild');
            return child;
        }
        return originalRemoveChild.call(this, child);
    };
    const originalInsertBefore = Node.prototype.insertBefore;
    Node.prototype.insertBefore = function guardedInsertBefore(newNode, referenceNode) {
        // `insertBefore(node, null)` is a valid append and must pass through — only
        // a non-null reference whose parent is no longer `this` is the anomaly.
        // Canonical patch: no-op (return the node without inserting) rather than
        // throw. Reappears on the next full render.
        if (referenceNode && referenceNode.parentNode !== this) {
            warnOnce('insertBefore');
            return newNode;
        }
        return originalInsertBefore.call(this, newNode, referenceNode);
    };
};
